import { ThreadManager } from '../threads';
import { logger } from '../utils/logger';
import { STORAGE_CONFIG } from '../config/storage';

/**
 * CleanupService handles cleanup of old threads and UIBlocks
 * to prevent memory bloat and maintain optimal performance
 */
export class CleanupService {
  private static instance: CleanupService;
  private cleanupInterval: NodeJS.Timeout | null = null;

  private constructor() {}

  /**
   * Get singleton instance of CleanupService
   */
  static getInstance(): CleanupService {
    if (!CleanupService.instance) {
      CleanupService.instance = new CleanupService();
    }
    return CleanupService.instance;
  }

  /**
   * Clean up old threads based on retention period
   * @param retentionDays - Number of days to keep threads (defaults to config)
   * @returns Number of threads deleted
   */
  cleanupOldThreads(retentionDays: number = STORAGE_CONFIG.THREAD_RETENTION_DAYS): number {
    const threadManager = ThreadManager.getInstance();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - retentionDays);

    const threads = threadManager.getAllThreads();
    let deletedCount = 0;

    for (const thread of threads) {
      if (thread.getCreatedAt() < cutoffDate) {
        const threadId = thread.getId();
        if (threadManager.deleteThread(threadId)) {
          deletedCount++;
          logger.info(`Deleted old thread: ${threadId} (created: ${thread.getCreatedAt().toISOString()})`);
        }
      }
    }

    if (deletedCount > 0) {
      logger.info(`Cleanup: Deleted ${deletedCount} old threads (older than ${retentionDays} days)`);
    }

    return deletedCount;
  }

  /**
   * Clean up old UIBlocks within threads based on retention period
   * @param retentionDays - Number of days to keep UIBlocks (defaults to config)
   * @returns Object with number of UIBlocks deleted per thread
   */
  cleanupOldUIBlocks(retentionDays: number = STORAGE_CONFIG.UIBLOCK_RETENTION_DAYS): { [threadId: string]: number } {
    const threadManager = ThreadManager.getInstance();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - retentionDays);

    const threads = threadManager.getAllThreads();
    const deletionStats: { [threadId: string]: number } = {};

    for (const thread of threads) {
      const uiblocks = thread.getUIBlocks();
      let deletedInThread = 0;

      for (const uiblock of uiblocks) {
        if (uiblock.getCreatedAt() < cutoffDate) {
          if (thread.removeUIBlock(uiblock.getId())) {
            deletedInThread++;
          }
        }
      }

      if (deletedInThread > 0) {
        deletionStats[thread.getId()] = deletedInThread;
        logger.info(
          `Deleted ${deletedInThread} old UIBlocks from thread ${thread.getId()} (older than ${retentionDays} days)`
        );
      }
    }

    const totalDeleted = Object.values(deletionStats).reduce((sum, count) => sum + count, 0);
    if (totalDeleted > 0) {
      logger.info(`Cleanup: Deleted ${totalDeleted} old UIBlocks across ${Object.keys(deletionStats).length} threads`);
    }

    return deletionStats;
  }

  /**
   * Clear all component data from UIBlocks to free memory
   * Keeps metadata but removes the actual data
   * @param retentionDays - Number of days to keep full data (defaults to config)
   * @returns Number of UIBlocks whose data was cleared
   */
  clearOldUIBlockData(retentionDays: number = STORAGE_CONFIG.UIBLOCK_RETENTION_DAYS): number {
    const threadManager = ThreadManager.getInstance();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - retentionDays);

    const threads = threadManager.getAllThreads();
    let clearedCount = 0;

    for (const thread of threads) {
      const uiblocks = thread.getUIBlocks();

      for (const uiblock of uiblocks) {
        if (uiblock.getCreatedAt() < cutoffDate) {
          const componentData = uiblock.getComponentData();

          // Only clear if data exists
          if (componentData && Object.keys(componentData).length > 0) {
            // Keep metadata but clear actual data
            const metadata = {
              dataCleared: true,
              clearedAt: new Date().toISOString(),
              originalDataInfo: {
                totalRows: componentData.totalRows,
                storedRows: componentData.storedRows,
                isTruncated: componentData.isTruncated,
              },
            };

            uiblock.setComponentData({ ...metadata, data: null });
            clearedCount++;
          }
        }
      }
    }

    if (clearedCount > 0) {
      logger.info(`Cleanup: Cleared data from ${clearedCount} old UIBlocks (older than ${retentionDays} days)`);
    }

    return clearedCount;
  }

  /**
   * Run full cleanup (threads, UIBlocks, and data)
   * @returns Cleanup statistics
   */
  runFullCleanup(): {
    threadsDeleted: number;
    uiblocksDeleted: { [threadId: string]: number };
    dataCleared: number;
  } {
    logger.info('Starting full cleanup...');

    const stats = {
      threadsDeleted: this.cleanupOldThreads(),
      uiblocksDeleted: this.cleanupOldUIBlocks(),
      dataCleared: this.clearOldUIBlockData(),
    };

    const totalUIBlocksDeleted = Object.values(stats.uiblocksDeleted).reduce((sum, count) => sum + count, 0);

    logger.info(
      `Full cleanup completed: ${stats.threadsDeleted} threads, ${totalUIBlocksDeleted} UIBlocks deleted, ${stats.dataCleared} UIBlock data cleared`
    );

    return stats;
  }

  /**
   * Start automatic cleanup at regular intervals
   * @param intervalHours - Hours between cleanup runs (default: 24)
   */
  startAutoCleanup(intervalHours: number = 24): void {
    if (this.cleanupInterval) {
      logger.warn('Auto cleanup is already running');

      //stop this and run with new interval

      
      return;
    }

    const intervalMs = intervalHours * 60 * 60 * 1000;

    // Run initial cleanup
    this.runFullCleanup();

    // Schedule recurring cleanup
    this.cleanupInterval = setInterval(() => {
      this.runFullCleanup();
    }, intervalMs);

    logger.info(`Auto cleanup started: running every ${intervalHours} hours`);
  }

  /**
   * Stop automatic cleanup
   */
  stopAutoCleanup(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
      logger.info('Auto cleanup stopped');
    }
  }

  /**
   * Check if auto cleanup is running
   */
  isAutoCleanupRunning(): boolean {
    return this.cleanupInterval !== null;
  }

  /**
   * Get current memory usage statistics
   */
  getMemoryStats(): {
    threadCount: number;
    totalUIBlocks: number;
    avgUIBlocksPerThread: number;
  } {
    const threadManager = ThreadManager.getInstance();
    const threads = threadManager.getAllThreads();
    const threadCount = threads.length;

    let totalUIBlocks = 0;
    for (const thread of threads) {
      totalUIBlocks += thread.getUIBlockCount();
    }

    return {
      threadCount,
      totalUIBlocks,
      avgUIBlocksPerThread: threadCount > 0 ? totalUIBlocks / threadCount : 0,
    };
  }
}
